/* eslint no-console: ["off"] */
import { ILogger, LogLevel } from '@studio/domain/logger';
import { formatTime } from '../utils';
import { consoleColor } from './console-colors';
import type { HttpErrorResponse } from '@angular/common/http';

/**
 * Logging utility class
 * inspiration gathered from Nest.js Logging https://docs.nestjs.com/techniques/logger
 */
export class Logger implements ILogger {
    private static _logLevel: LogLevel = LogLevel.ERROR; // start with the highest log level. Will be set once the app initialized.
    static get LogLevel(): LogLevel {
        return Logger._logLevel;
    }
    private static lastTimestamp?: number;
    protected static instance?: typeof Logger | ILogger = Logger;

    constructor(
        protected context?: string,
        private readonly isTimestampEnabled: boolean = false
    ) {}

    group(message: string, context?: string): void {
        if (!Logger.isLogLevelEnabled(LogLevel.LOG)) {
            return;
        }
        this.callFunction('group', message, context);
    }

    groupEnd(): void {
        if (!Logger.isLogLevelEnabled(LogLevel.LOG)) {
            return;
        }
        Logger.groupEnd();
    }

    log(message: any, context?: string): void {
        this.callFunction('log', message, context);
    }

    debug(message: any, context?: string): void {
        this.callFunction('debug', message, context);
    }

    verbose(message: any, context?: string): void {
        this.callFunction('verbose', message, context);
    }

    warn(message: any, context?: string): void {
        this.callFunction('warn', message, context);
    }

    error(message: any, trace: string = '', context?: string): void {
        const instance = this.getInstance();
        instance &&
            instance.error.call(
                instance,
                message,
                trace,
                context || this.context,
                this.isTimestampEnabled
            );
    }

    setContext(context: string): void {
        this.context = context;
    }

    static getLogLevelName(): string {
        switch (Logger._logLevel) {
            case LogLevel.DEBUG:
                return 'DEBUG';
            case LogLevel.LOG:
                return 'LOG';
            case LogLevel.VERBOSE:
                return 'VERBOSE';
            case LogLevel.WARN:
                return 'WARNING';
            case LogLevel.ERROR:
                return 'ERROR';
            default:
                return `${Logger._logLevel} not handled`;
        }
    }

    static setLogLevel(level: LogLevel): void {
        Logger._logLevel = level;
    }

    getTimestamp(): string {
        return Logger.getTimestamp();
    }

    static group(message: any): void {
        console.groupCollapsed(message);
    }

    static groupEnd(): void {
        console.groupEnd();
    }

    static isLogLevelEnabled(level: LogLevel): boolean {
        return level >= Logger._logLevel;
    }

    static log(message: any, context: string = '', isTimeDiffEnabled: boolean = true): void {
        if (!Logger.isLogLevelEnabled(LogLevel.LOG)) {
            return;
        }
        this.printMessage('log', message, undefined, context, isTimeDiffEnabled);
    }

    static debug(message: any, context: string = '', isTimeDiffEnabled: boolean = true): void {
        if (!Logger.isLogLevelEnabled(LogLevel.DEBUG)) {
            return;
        }
        this.printMessage('debug', message, consoleColor.blue, context, isTimeDiffEnabled);
    }

    static verbose(message: any, context: string = '', isTimeDiffEnabled: boolean = true): void {
        if (!Logger.isLogLevelEnabled(LogLevel.VERBOSE)) {
            return;
        }
        this.printMessage('log', message, consoleColor.cyanBright, context, isTimeDiffEnabled);
    }

    static warn(message: any, context: string = '', isTimeDiffEnabled: boolean = true): void {
        if (!Logger.isLogLevelEnabled(LogLevel.WARN)) {
            return;
        }
        this.printMessage('warn', message, consoleColor.yellow, context, isTimeDiffEnabled);
    }

    static error(
        message: any,
        trace: string = '',
        context: string = '',
        isTimeDiffEnabled: boolean = true
    ): void {
        if (!Logger.isLogLevelEnabled(LogLevel.ERROR)) {
            return;
        }
        this.printMessage('error', message, consoleColor.red, context, isTimeDiffEnabled);
        this.printStackTrace(trace);
    }

    static getTimestamp(): string {
        return formatTime(Date.now());
    }

    private callFunction(
        name: 'group' | 'log' | 'warn' | 'debug' | 'verbose',
        message: any,
        context?: string
    ): void {
        const instance = this.getInstance();
        const func = instance && (instance as typeof Logger)[name];
        func && func.call(instance, message, context || this.context, this.isTimestampEnabled);
    }

    protected getInstance(): ILogger | typeof Logger | undefined {
        const { instance } = Logger;
        return instance === this ? Logger : instance;
    }

    private static printMessage(
        level: 'log' | 'warn' | 'debug' | 'error',
        message: any,
        color: string | undefined,
        context: string = '',
        isTimeDiffEnabled?: boolean
    ): void {
        let output = message;
        if (isPlainObject(message)) {
            output = `Object:\n${JSON.stringify(message, null, 2)}\n`;
        } else if (isError(message)) {
            output = message.message;
        }

        const contextColor = context ? consoleColor.yellow : '';
        const contextMessage = context ? `%c[${context}] ` : '';
        const timestampDiff = this.updateAndGetTimestampDiff(isTimeDiffEnabled);
        const timestampColor = timestampDiff.length ? consoleColor.white : '';
        const instance = (this.instance as typeof Logger) ?? Logger;
        const timestamp = instance.getTimestamp ? instance.getTimestamp() : Logger.getTimestamp?.();
        const computedMessage = `${timestamp} ${contextMessage} %c${output} ${timestampDiff}\n`;

        // log complex objects more detailed
        if (isObject(message) && !isPlainObject(message)) {
            Logger.logMessage('groupCollapsed', computedMessage, contextColor, color, timestampColor);

            isError(message) || isHttpError(message) ? console.error(message) : console.dir(message);

            console.groupEnd();
        } else {
            Logger.logMessage(level, computedMessage, contextColor, color, timestampColor);
        }
    }

    private static logMessage(
        level: string,
        message: string,
        contextColor: string | undefined,
        color: string | undefined,
        timestampColor: string
    ): void {
        color = level === 'warn' || level === 'error' ? undefined : color;

        if (!contextColor) {
            console[level](message, color, timestampColor);
        } else {
            console[level](message, contextColor, color, timestampColor);
        }
    }

    private static updateAndGetTimestampDiff(isTimeDiffEnabled?: boolean): string {
        let result = '';
        if (Logger.lastTimestamp && isTimeDiffEnabled) {
            result = `%c +${Date.now() - Logger.lastTimestamp}ms `;
            Logger.lastTimestamp = Date.now();
            return result;
        }
        Logger.lastTimestamp = Date.now();
        return result;
    }

    private static printStackTrace(trace: string): void {
        if (!trace) {
            return;
        }
        console.error(trace);
    }

    static testLogs(context?: string): void {
        this.debug('Debug message!', context);
        this.debug({ test: 123, debug: true }, context);
        this.verbose('Verbose message!', context);
        this.verbose({ test: 123, verbose: true }, context);
        this.log('Log message!', context);
        this.log({ test: 123, log: true }, context);
        this.warn('Warn message!', context);
        this.warn(new Error('New Warning'), context, false);
        this.error('Error message!', undefined, context);
        this.error(new Error('New Error'), undefined, context, false);
        throw new Error('Native Error thrown!');
    }
}

const isError = (obj: any): obj is Error =>
    obj instanceof Error ||
    obj instanceof ErrorEvent ||
    (isObject(obj) && (obj as any).error instanceof ErrorEvent);

const isHttpError = (obj: any): obj is HttpErrorResponse => 'status' in obj && obj.status >= 400;

const isUndefined = (obj: any): obj is undefined => typeof obj === 'undefined';

const isNil = (obj: any): obj is null | undefined => isUndefined(obj) || obj === null;

const isObject = (fn: any): fn is object => !isNil(fn) && typeof fn === 'object';

const isPlainObject = (fn: any): fn is object => {
    if (!isObject(fn)) {
        return false;
    }
    const proto = Object.getPrototypeOf(fn);
    if (proto === null) {
        return true;
    }
    const ctor = Object.prototype.hasOwnProperty.call(proto, 'constructor') && proto.constructor;
    return (
        typeof ctor === 'function' &&
        ctor instanceof ctor &&
        Function.prototype.toString.call(ctor) === Function.prototype.toString.call(Object)
    );
};

// make these methods available inside Studio
if (typeof window !== 'undefined') {
    // @ts-expect-error
    window.logger = Logger || {};
}
